import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Tooltip,
  Divider,
  Switch,
  Stack,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
} from '@mui/material';
import {
  Save,
  Undo,
  ChevronLeft as ChevronLeftIcon,
  Menu as MenuIcon,
  Schedule,
  Close,
  LightMode,
  WbTwilight,
  Nightlight,
  DarkMode,
  CheckCircle,
  RadioButtonUnchecked,
  Settings,
  Lock,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import EmployeeLayout from '../components/layout/EmployeeLayout';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import { useUnsavedChanges } from '../context/UnsavedChangesContext';
import { useEmployees } from '../hooks/useEmployees';
import { useShops } from '../hooks/useShops';
import { useEmployeeSchedules } from '../hooks/useEmployeeSchedules';
import { employeesService } from '../services/firebase/employees.service';
import DateNavigator from '../components/common/DateNavigator';
import { TimeRestrictions } from '../types/employee.types';
import { DayOfWeek } from '../types/common.types';

const dayOfWeekOrder: DayOfWeek[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const dayLabels: Record<DayOfWeek, string> = {
  sunday: 'Sun',
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
};

export default function EmployeeAvailabilityPage() {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();
  const { shops } = useShops();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    employees,
    updateMultipleDateAvailability,
    isLoading,
  } = useEmployees();

  // Get organization settings for employee permissions
  const allowSetHours = organization?.settings?.allowEmployeeSetHours ?? true;
  const allowSetTimeRestrictions = organization?.settings?.allowEmployeeSetTimeRestrictions ?? true;

  // Week navigation state (Monday-based)
  const [weekStart, setWeekStart] = useState<Date>(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = day === 0 ? -6 : -(day - 1);
    const start = new Date(today);
    start.setDate(today.getDate() + diff);
    start.setHours(0, 0, 0, 0);
    return start;
  });

  // Calculate week end for schedules query
  const weekEnd = useMemo(() => {
    const end = new Date(weekStart);
    end.setDate(weekStart.getDate() + 6);
    return end;
  }, [weekStart]);

  // Helper to format date as YYYY-MM-DD (declared early for use in schedules query)
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fetch schedules for the current week to check for locked days
  const { schedules: employeeShifts } = useEmployeeSchedules(
    user?.employeeId,
    formatDate(weekStart),
    formatDate(weekEnd)
  );

  // Create a set of dates that have shifts (locked days)
  const lockedDates = useMemo(() => {
    const dates = new Set<string>();
    employeeShifts.forEach((shift) => {
      dates.add(shift.date);
    });
    return dates;
  }, [employeeShifts]);

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Local state for hours
  const [hoursState, setHoursState] = useState<Record<string, number | string>>({});

  // Local state for preferences (time restrictions and notes)
  const [preferencesState, setPreferencesState] = useState<
    Record<string, { timeRestrictions?: TimeRestrictions; notes?: string }>
  >({});

  // Original states for revert functionality
  const [originalHoursState, setOriginalHoursState] = useState<Record<string, number | string>>({});
  const [originalPreferencesState, setOriginalPreferencesState] = useState<
    Record<string, { timeRestrictions?: TimeRestrictions; notes?: string }>
  >({});

  // Preferences dialog state
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [preferencesForm, setPreferencesForm] = useState({
    timeRestrictions: {
      morning: false,
      afternoon: false,
      evening: false,
      night: false,
    },
    notes: '',
  });

  const [isSaving, setIsSaving] = useState(false);

  // Track if we've initialized state for the current week (prevents re-initialization on data refetch)
  const initializedWeekRef = useRef<string | null>(null);

  // Find the current employee record
  const currentEmployee = useMemo(() => {
    if (!user?.employeeId) return null;
    return employees.find((emp) => emp.id === user.employeeId);
  }, [user?.employeeId, employees]);

  // Helper to create state key
  const getStateKey = (date: Date): string => {
    return formatDate(date);
  };

  // Get dates for the current week (memoized to prevent recreation on every render)
  const weekDates = useMemo(() => {
    const dates: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [weekStart]);

  // Initialize hours and preferences state from employee data
  // Only reinitialize when weekStart changes, not when currentEmployee is refetched
  const weekKey = formatDate(weekStart);
  useEffect(() => {
    if (!currentEmployee) return;

    // Skip if we've already initialized state for this week
    // This prevents UI flicker when data is refetched after save
    if (initializedWeekRef.current === weekKey) {
      return;
    }

    const newHoursState: Record<string, number | string> = {};
    const newPrefsState: Record<string, { timeRestrictions?: TimeRestrictions; notes?: string }> = {};

    weekDates.forEach((date) => {
      const dateKey = formatDate(date);
      const availability = employeesService.getDateAvailability(currentEmployee, dateKey);
      if (availability) {
        newHoursState[dateKey] = availability.hoursAvailable;
        if (availability.timeRestrictions || availability.notes) {
          newPrefsState[dateKey] = {
            timeRestrictions: availability.timeRestrictions,
            notes: availability.notes,
          };
        }
      }
    });

    setHoursState(newHoursState);
    setOriginalHoursState(newHoursState);
    setPreferencesState(newPrefsState);
    setOriginalPreferencesState(newPrefsState);

    // Mark this week as initialized
    initializedWeekRef.current = weekKey;
  }, [currentEmployee, weekKey, weekDates]);

  // Check if there are unsaved changes
  const hasUnsavedChanges = useCallback((): boolean => {
    const currentKeys = Object.keys(hoursState);
    const originalKeys = Object.keys(originalHoursState);

    if (currentKeys.length !== originalKeys.length) return true;

    for (const key of currentKeys) {
      const currentVal = hoursState[key]?.toString() || '';
      const originalVal = originalHoursState[key]?.toString() || '';
      if (currentVal !== originalVal) return true;
    }

    for (const key of originalKeys) {
      if (!(key in hoursState)) return true;
    }

    // Check preferences changes
    const currentPrefKeys = Object.keys(preferencesState);
    const originalPrefKeys = Object.keys(originalPreferencesState);

    if (currentPrefKeys.length !== originalPrefKeys.length) return true;

    for (const key of currentPrefKeys) {
      const current = preferencesState[key];
      const original = originalPreferencesState[key];
      if (!original) return true;
      if (JSON.stringify(current) !== JSON.stringify(original)) return true;
    }

    for (const key of originalPrefKeys) {
      if (!(key in preferencesState)) return true;
    }

    return false;
  }, [hoursState, originalHoursState, preferencesState, originalPreferencesState]);

  // Sync unsaved changes state with context for navigation blocking
  const { setHasUnsavedChanges } = useUnsavedChanges();

  useEffect(() => {
    setHasUnsavedChanges(hasUnsavedChanges());
  }, [hasUnsavedChanges, setHasUnsavedChanges]);

  // Warn user when trying to leave with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Get count of changes
  const getChangesCount = (): number => {
    let count = 0;
    const allKeys = new Set([...Object.keys(hoursState), ...Object.keys(originalHoursState)]);

    for (const key of allKeys) {
      const currentVal = hoursState[key]?.toString() || '';
      const originalVal = originalHoursState[key]?.toString() || '';
      if (currentVal !== originalVal) count++;
    }

    return count;
  };

  // Validate hours value
  const isValidHours = (value: string | number | undefined): boolean => {
    if (value === undefined || value === '') return true;
    const num = parseFloat(value.toString());
    if (isNaN(num)) return false;
    return num >= 0 && num <= 24;
  };

  // Get validation error message
  const getHoursError = (value: string | number | undefined): string => {
    if (value === undefined || value === '') return '';
    const num = parseFloat(value.toString());
    if (isNaN(num)) return 'Invalid number';
    if (num < 0) return 'Cannot be negative';
    if (num > 24) return 'Cannot exceed 24';
    return '';
  };

  // Check if there are any validation errors
  const hasValidationErrors = (): boolean => {
    for (const key of Object.keys(hoursState)) {
      if (!isValidHours(hoursState[key])) return true;
    }
    return false;
  };

  // Get count of validation errors
  const getValidationErrorsCount = (): number => {
    let count = 0;
    for (const key of Object.keys(hoursState)) {
      if (!isValidHours(hoursState[key])) count++;
    }
    return count;
  };

  // Check if a cell has changes
  const cellHasChanges = (date: Date): boolean => {
    const key = getStateKey(date);
    const currentVal = hoursState[key]?.toString() || '';
    const originalVal = originalHoursState[key]?.toString() || '';
    if (currentVal !== originalVal) return true;

    const currentPrefs = preferencesState[key];
    const originalPrefs = originalPreferencesState[key];
    if (JSON.stringify(currentPrefs || {}) !== JSON.stringify(originalPrefs || {})) {
      return true;
    }

    return false;
  };

  // Revert all changes
  const handleRevertChanges = () => {
    setHoursState({ ...originalHoursState });
    setPreferencesState({ ...originalPreferencesState });
    enqueueSnackbar('Changes reverted', { variant: 'info' });
  };

  // Revert a specific cell
  const handleRevertCell = (date: Date) => {
    const key = getStateKey(date);
    const originalVal = originalHoursState[key];
    setHoursState((prev) => {
      const newState = { ...prev };
      if (originalVal !== undefined) {
        newState[key] = originalVal;
      } else {
        delete newState[key];
      }
      return newState;
    });

    const originalPrefs = originalPreferencesState[key];
    setPreferencesState((prev) => {
      const newState = { ...prev };
      if (originalPrefs !== undefined) {
        newState[key] = { ...originalPrefs };
      } else {
        delete newState[key];
      }
      return newState;
    });
  };

  // Calculate total hours for the week
  const getTotalHours = (): number => {
    let total = 0;
    for (const date of weekDates) {
      const key = getStateKey(date);
      const hoursValue = hoursState[key];
      if (hoursValue !== undefined && hoursValue !== '') {
        const hours = parseFloat(hoursValue.toString());
        if (!isNaN(hours) && hours >= 0) {
          total += hours;
        }
      }
    }
    return total;
  };

  // Save all changes using bulk update
  const handleSaveAllChanges = async () => {
    if (!currentEmployee) return;

    setIsSaving(true);
    try {
      const allKeys = new Set([
        ...Object.keys(hoursState),
        ...Object.keys(originalHoursState),
        ...Object.keys(preferencesState),
        ...Object.keys(originalPreferencesState),
      ]);

      // Collect all updates for bulk operation
      const updates: Array<{ date: string; availability: { date: string; hoursAvailable: number; shopId: string; timeRestrictions?: TimeRestrictions; notes?: string } | null }> = [];

      for (const dateKey of allKeys) {
        const currentHours = hoursState[dateKey]?.toString() || '';
        const originalHours = originalHoursState[dateKey]?.toString() || '';
        const currentPrefs = preferencesState[dateKey];
        const originalPrefs = originalPreferencesState[dateKey];

        const hoursChanged = currentHours !== originalHours;
        const prefsChanged = JSON.stringify(currentPrefs || {}) !== JSON.stringify(originalPrefs || {});

        if (hoursChanged || prefsChanged) {
          const hours = parseFloat(currentHours || '0');

          if (isNaN(hours) || hours < 0 || currentHours === '') {
            // Remove availability if no hours and no preferences
            if (!currentPrefs?.timeRestrictions && !currentPrefs?.notes) {
              const existingAvailability = employeesService.getDateAvailability(
                currentEmployee,
                dateKey
              );
              if (existingAvailability) {
                updates.push({ date: dateKey, availability: null });
              }
            }
          } else {
            // Save or update availability
            const shopId = currentEmployee.assignedShopIds?.[0] || shops[0]?.id || 'default';

            updates.push({
              date: dateKey,
              availability: {
                date: dateKey,
                hoursAvailable: hours,
                shopId,
                timeRestrictions: currentPrefs?.timeRestrictions,
                notes: currentPrefs?.notes,
              },
            });
          }
        }
      }

      // Perform single bulk update
      if (updates.length > 0) {
        await updateMultipleDateAvailability(currentEmployee.id, updates);
      }

      setOriginalHoursState({ ...hoursState });
      setOriginalPreferencesState({ ...preferencesState });
      enqueueSnackbar('Availability saved successfully!', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to save changes', { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  // Check if a date is today
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Check if a date is in the past
  const isPastDate = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  // Navigation handler for DateNavigator
  const handleDateChange = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    setWeekStart(date);
  };

  // Handle hours change
  const handleHoursChange = (date: Date, value: string) => {
    const key = getStateKey(date);
    setHoursState((prev) => ({ ...prev, [key]: value }));
  };

  // Handle simple availability toggle (when hours setting is disabled)
  const handleAvailabilityToggle = (date: Date, isAvailable: boolean) => {
    const key = getStateKey(date);
    // When available, set to 8 hours (standard work day). When not available, set to 0.
    setHoursState((prev) => ({ ...prev, [key]: isAvailable ? 8 : 0 }));
  };

  // Check if a date is marked as available (for simple toggle mode)
  const isDateAvailable = (date: Date): boolean => {
    const key = getStateKey(date);
    const hoursValue = hoursState[key];
    if (hoursValue === undefined || hoursValue === '') return false;
    const hours = parseFloat(hoursValue.toString());
    return !isNaN(hours) && hours > 0;
  };

  // Check if a date can be edited (not past and no shift)
  const canEditDate = (date: Date): boolean => {
    if (isPastDate(date)) return false;
    const dateKey = formatDate(date);
    if (lockedDates.has(dateKey)) return false;
    return true;
  };

  // Handle keyboard navigation (Tab/Shift+Tab)
  const handleKeyDown = (e: React.KeyboardEvent, dateIndex: number) => {
    if (e.key === 'Tab') {
      e.preventDefault();

      // Find next/previous editable cell
      const findNextEditableCell = (startIndex: number, forward: boolean): number | null => {
        let idx = startIndex;

        while (true) {
          if (forward) {
            idx++;
            if (idx >= 7) return null; // No more cells
          } else {
            idx--;
            if (idx < 0) return null; // No more cells
          }

          // Check if this cell is editable
          const date = weekDates[idx];
          if (canEditDate(date)) {
            return idx;
          }
        }
      };

      if (e.shiftKey) {
        // Shift+Tab: Move backward
        const prev = findNextEditableCell(dateIndex, false);
        if (prev !== null) {
          const prevInput = document.getElementById(`hours-input-${prev}`);
          prevInput?.focus();
        }
      } else {
        // Tab: Move forward
        const next = findNextEditableCell(dateIndex, true);
        if (next !== null) {
          const nextInput = document.getElementById(`hours-input-${next}`);
          nextInput?.focus();
        }
      }
    }
  };

  // Open preferences dialog
  const handleOpenPreferences = (date: Date) => {
    if (isPastDate(date)) return;

    setSelectedDate(date);

    const key = getStateKey(date);
    const localPrefs = preferencesState[key];
    const dateKey = formatDate(date);
    const existingAvailability = currentEmployee
      ? employeesService.getDateAvailability(currentEmployee, dateKey)
      : null;

    setPreferencesForm({
      timeRestrictions: {
        morning:
          localPrefs?.timeRestrictions?.morning ??
          existingAvailability?.timeRestrictions?.morning ??
          false,
        afternoon:
          localPrefs?.timeRestrictions?.afternoon ??
          existingAvailability?.timeRestrictions?.afternoon ??
          false,
        evening:
          localPrefs?.timeRestrictions?.evening ??
          existingAvailability?.timeRestrictions?.evening ??
          false,
        night:
          localPrefs?.timeRestrictions?.night ??
          existingAvailability?.timeRestrictions?.night ??
          false,
      },
      notes: localPrefs?.notes ?? existingAvailability?.notes ?? '',
    });

    setPreferencesOpen(true);
  };

  // Save preferences to local state
  const handleSavePreferences = () => {
    if (!selectedDate) return;

    const key = getStateKey(selectedDate);
    const hasRestrictions = Object.values(preferencesForm.timeRestrictions).some((v) => v);

    const newPrefs: { timeRestrictions?: TimeRestrictions; notes?: string } = {};

    if (hasRestrictions) {
      newPrefs.timeRestrictions = {};
      if (preferencesForm.timeRestrictions.morning) newPrefs.timeRestrictions.morning = true;
      if (preferencesForm.timeRestrictions.afternoon) newPrefs.timeRestrictions.afternoon = true;
      if (preferencesForm.timeRestrictions.evening) newPrefs.timeRestrictions.evening = true;
      if (preferencesForm.timeRestrictions.night) newPrefs.timeRestrictions.night = true;
    }

    if (preferencesForm.notes && preferencesForm.notes.trim()) {
      newPrefs.notes = preferencesForm.notes.trim();
    }

    setPreferencesState((prev) => {
      const newState = { ...prev };
      if (Object.keys(newPrefs).length > 0) {
        newState[key] = newPrefs;
      } else {
        delete newState[key];
      }
      return newState;
    });

    enqueueSnackbar('Preferences updated (unsaved)', { variant: 'info' });
    setPreferencesOpen(false);
  };

  // Get restriction labels
  const getRestrictionLabel = (restrictions?: TimeRestrictions): string => {
    if (!restrictions) return '';
    const periods: string[] = [];
    if (restrictions.morning) periods.push('M');
    if (restrictions.afternoon) periods.push('A');
    if (restrictions.evening) periods.push('E');
    if (restrictions.night) periods.push('N');
    return periods.join('+');
  };

  const changesCount = getChangesCount();
  const hasChanges = hasUnsavedChanges();

  if (!currentEmployee && !isLoading) {
    return (
      <EmployeeLayout pageTitle="My Availability">
        <Alert severity="warning" sx={{ mt: 2 }}>
          Your employee profile could not be found. Please contact your manager.
        </Alert>
      </EmployeeLayout>
    );
  }

  // Render minimized sidebar (desktop only)
  const renderMinimizedSidebar = () => (
    <Paper
      sx={{
        position: 'fixed',
        left: 0,
        top: 64,
        bottom: 0,
        width: 64,
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        alignItems: 'center',
        py: 2,
        zIndex: 10,
        borderRadius: 0,
      }}
    >
      <Tooltip title="Expand Panel" placement="right">
        <IconButton onClick={() => setSidebarOpen(true)} sx={{ mb: 2 }}>
          <MenuIcon />
        </IconButton>
      </Tooltip>

      <Divider sx={{ width: '100%', my: 1 }} />

      <Tooltip
        title={hasValidationErrors() ? 'Fix errors first' : 'Save Changes'}
        placement="right"
      >
        <span>
          <IconButton
            onClick={handleSaveAllChanges}
            disabled={!hasChanges || isSaving || hasValidationErrors()}
            color={hasValidationErrors() ? 'error' : hasChanges ? 'primary' : 'default'}
            sx={{ mb: 1 }}
          >
            <Save />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Revert Changes" placement="right">
        <span>
          <IconButton
            onClick={handleRevertChanges}
            disabled={!hasChanges}
            color={hasChanges ? 'warning' : 'default'}
          >
            <Undo />
          </IconButton>
        </span>
      </Tooltip>

      {hasValidationErrors() ? (
        <Chip
          label={getValidationErrorsCount()}
          size="small"
          color="error"
          sx={{ mt: 1, minWidth: 24, height: 24 }}
        />
      ) : hasChanges ? (
        <Chip
          label={changesCount}
          size="small"
          color="primary"
          sx={{ mt: 1, minWidth: 24, height: 24 }}
        />
      ) : null}
    </Paper>
  );

  // Render expanded sidebar (desktop only)
  const renderExpandedSidebar = () => (
    <Paper
      sx={{
        position: 'fixed',
        left: 0,
        top: 64,
        bottom: 0,
        width: 280,
        p: 2,
        overflow: 'auto',
        zIndex: 10,
        borderRadius: 0,
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Actions
        </Typography>
        <IconButton size="small" onClick={() => setSidebarOpen(false)}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Save/Revert Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="overline" color="text.secondary">
          Changes
        </Typography>
        {hasValidationErrors() ? (
          <Chip
            label={`${getValidationErrorsCount()} error${getValidationErrorsCount() !== 1 ? 's' : ''}`}
            size="small"
            color="error"
          />
        ) : hasChanges ? (
          <Chip label={`${changesCount} unsaved`} size="small" color="warning" />
        ) : (
          <Typography variant="caption" color="text.secondary">
            No changes
          </Typography>
        )}
      </Box>

      <Tooltip
        title={hasValidationErrors() ? 'Fix validation errors before saving' : ''}
        placement="top"
      >
        <span>
          <Button
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={16} /> : <Save />}
            onClick={handleSaveAllChanges}
            disabled={!hasChanges || isSaving || hasValidationErrors()}
            fullWidth
            sx={{ mb: 1 }}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </span>
      </Tooltip>

      <Button
        variant="outlined"
        startIcon={<Undo />}
        onClick={handleRevertChanges}
        disabled={!hasChanges}
        fullWidth
        color="warning"
        sx={{ mb: 3 }}
      >
        Revert Changes
      </Button>

      <Divider sx={{ mb: 2 }} />

      {/* Weekly Summary */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="overline" color="text.secondary">
          Weekly Summary
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Schedule color="primary" />
          <Typography variant="h5" fontWeight={600} color="primary.main">
            {getTotalHours()}h
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Total hours this week
        </Typography>
      </Paper>

      <Divider sx={{ mb: 2 }} />

      {/* Modified Days List */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="overline" color="text.secondary">
          Modified Days
        </Typography>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {!hasChanges ? (
          <Typography variant="body2" color="text.disabled" fontStyle="italic">
            No pending changes
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {weekDates
              .filter((date) => cellHasChanges(date))
              .map((date) => (
                <Box
                  key={formatDate(date)}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    bgcolor: 'warning.50',
                    border: '1px solid',
                    borderColor: 'warning.light',
                  }}
                >
                  <Typography variant="caption" fontWeight={500}>
                    {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </Typography>
                </Box>
              ))}
          </Box>
        )}
      </Box>

      {/* Help Text */}
      <Box sx={{ mt: 'auto', pt: 2 }}>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Enter your available hours per day. Use the settings icon to set time preferences.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Lock sx={{ fontSize: 14, color: 'primary.main' }} />
          <Typography variant="caption" color="text.secondary">
            Days with scheduled shifts are locked
          </Typography>
        </Box>
      </Box>
    </Paper>
  );

  return (
    <EmployeeLayout pageTitle="My Availability">
      {/* Sidebar */}
      {sidebarOpen ? renderExpandedSidebar() : renderMinimizedSidebar()}

      {/* Main content with margin for fixed sidebar */}
      <Box sx={{ ml: { xs: 0, md: sidebarOpen ? '280px' : '64px' } }}>
        {/* Header */}
        <Box sx={{ mb: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography
              variant="h4"
              fontWeight={600}
              sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
            >
              My Availability
            </Typography>
            {/* Mobile action buttons */}
            {isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title="Revert changes">
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleRevertChanges}
                      disabled={!hasChanges}
                      color={hasChanges ? 'warning' : 'default'}
                      sx={{ border: '1px solid', borderColor: 'divider' }}
                    >
                      <Undo fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : <Save />}
                  onClick={handleSaveAllChanges}
                  disabled={!hasChanges || isSaving || hasValidationErrors()}
                >
                  Save
                </Button>
              </Box>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
            Set your available hours and time preferences for each day
          </Typography>
        </Box>

        {/* Week Navigator */}
        <DateNavigator
          mode="weekly"
          selectedDate={formatDate(weekStart)}
          onDateChange={handleDateChange}
        />

        {/* Mobile Summary Bar */}
        {isMobile && (
          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Schedule color="primary" sx={{ fontSize: 20 }} />
              <Box>
                <Typography variant="body2" fontWeight={600} color="primary.main">
                  {getTotalHours()}h total
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  This week
                </Typography>
              </Box>
            </Box>
            {hasValidationErrors() ? (
              <Chip
                label={`${getValidationErrorsCount()} error${getValidationErrorsCount() !== 1 ? 's' : ''}`}
                size="small"
                color="error"
              />
            ) : hasChanges ? (
              <Chip label={`${changesCount} unsaved`} size="small" color="warning" />
            ) : (
              <Chip label="Saved" size="small" color="success" variant="outlined" />
            )}
          </Paper>
        )}

        {/* Availability Table/Cards */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : isMobile ? (
          /* Mobile: Card-based layout */
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {weekDates.map((date, dateIndex) => {
              const day = dayOfWeekOrder[dateIndex];
              const key = getStateKey(date);
              const hoursValue = hoursState[key] ?? '';
              const localPrefs = preferencesState[key];
              const dateKey = formatDate(date);
              const availability = currentEmployee
                ? employeesService.getDateAvailability(currentEmployee, dateKey)
                : null;

              const restrictionLabel = getRestrictionLabel(
                localPrefs?.timeRestrictions ?? availability?.timeRestrictions
              );

              const hours = parseFloat(hoursValue?.toString() || '0');
              const isOff = hours === 0 && hoursValue !== '';
              const hasCellChanges = cellHasChanges(date);
              const hasLocalData = hoursValue !== '' || localPrefs;
              const hoursError = getHoursError(hoursValue);
              const hasError = hoursError !== '';
              const isPast = isPastDate(date);
              const hasShift = lockedDates.has(dateKey);
              const isLocked = isPast || hasShift;
              const isTodayCell = isToday(date);

              return (
                <Card
                  key={dateIndex}
                  variant="outlined"
                  sx={{
                    borderColor: hasShift
                      ? 'primary.main'
                      : hasError
                      ? 'error.main'
                      : hasCellChanges
                      ? 'warning.main'
                      : isTodayCell
                      ? 'primary.light'
                      : 'divider',
                    borderWidth: hasShift || hasError || hasCellChanges || isTodayCell ? 2 : 1,
                    opacity: isPast ? 0.6 : 1,
                    bgcolor: hasShift
                      ? 'primary.50'
                      : hasError
                      ? 'error.lighter'
                      : isOff
                      ? 'grey.100'
                      : hasLocalData || availability
                      ? 'success.lighter'
                      : 'background.paper',
                  }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      {/* Date column */}
                      <Box sx={{ textAlign: 'center', minWidth: 50 }}>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color={isTodayCell ? 'primary.main' : 'text.secondary'}
                          sx={{ textTransform: 'uppercase' }}
                        >
                          {dayLabels[day]}
                        </Typography>
                        <Typography
                          variant="h5"
                          fontWeight={700}
                          color={isTodayCell ? 'primary.main' : 'text.primary'}
                        >
                          {date.getDate()}
                        </Typography>
                      </Box>

                      {/* Content column */}
                      <Box sx={{ flex: 1 }}>
                        {/* Shift indicator */}
                        {hasShift && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <Lock sx={{ fontSize: 16, color: 'primary.main' }} />
                            <Typography variant="caption" color="primary.main" fontWeight={600}>
                              Shift Scheduled
                            </Typography>
                          </Box>
                        )}

                        {/* Hours Input or Simple Toggle */}
                        {allowSetHours ? (
                          <TextField
                            id={`hours-input-${dateIndex}`}
                            type="number"
                            value={hoursValue}
                            onChange={(e) => handleHoursChange(date, e.target.value)}
                            onFocus={(e) => e.target.select()}
                            disabled={isLocked}
                            size="small"
                            placeholder="Hours available"
                            error={hasError}
                            helperText={hoursError}
                            inputProps={{
                              min: 0,
                              max: 24,
                              step: 0.5,
                            }}
                            sx={{ width: '100%', mb: 1 }}
                          />
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Switch
                              checked={isDateAvailable(date)}
                              onChange={(e) => handleAvailabilityToggle(date, e.target.checked)}
                              disabled={isLocked}
                              color="success"
                            />
                            <Typography variant="body2" color={isDateAvailable(date) ? 'success.main' : 'text.secondary'}>
                              {isDateAvailable(date) ? 'Available' : 'Not Available'}
                            </Typography>
                          </Box>
                        )}

                        {/* Status indicators */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          {allowSetHours && isOff && !hasShift && (
                            <Chip label="OFF/Holiday" size="small" color="default" />
                          )}
                          {allowSetTimeRestrictions && restrictionLabel && (
                            <Chip
                              label={restrictionLabel}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          )}
                          {isPast && !hasShift && (
                            <Chip label="Past" size="small" color="default" variant="outlined" />
                          )}
                        </Box>
                      </Box>

                      {/* Actions column */}
                      {!isLocked && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {allowSetTimeRestrictions && (
                            <Tooltip title="Time preferences">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenPreferences(date)}
                              >
                                <Settings fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {hasCellChanges && (
                            <Tooltip title="Revert">
                              <IconButton
                                size="small"
                                onClick={() => handleRevertCell(date)}
                                color="warning"
                              >
                                <Undo fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        ) : (
          /* Desktop: Table layout */
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  {weekDates.map((date, index) => {
                    const day = dayOfWeekOrder[index];
                    const isTodayCell = isToday(date);
                    return (
                      <TableCell key={day} align="center" sx={{ fontWeight: 700 }}>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color={isTodayCell ? 'primary.main' : 'text.primary'}
                        >
                          {dayLabels[day]}
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight={600}
                          color={isTodayCell ? 'primary.main' : 'text.primary'}
                        >
                          {date.getDate()}
                        </Typography>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  {weekDates.map((date, dateIndex) => {
                    const key = getStateKey(date);
                    const hoursValue = hoursState[key] ?? '';
                    const localPrefs = preferencesState[key];
                    const dateKey = formatDate(date);
                    const availability = currentEmployee
                      ? employeesService.getDateAvailability(currentEmployee, dateKey)
                      : null;

                    const restrictionLabel = getRestrictionLabel(
                      localPrefs?.timeRestrictions ?? availability?.timeRestrictions
                    );

                    const hours = parseFloat(hoursValue?.toString() || '0');
                    const isOff = hours === 0 && hoursValue !== '';
                    const hasCellChanges = cellHasChanges(date);
                    const hasLocalData = hoursValue !== '' || localPrefs;
                    const hoursError = getHoursError(hoursValue);
                    const hasError = hoursError !== '';
                    const isPast = isPastDate(date);
                    const hasShift = lockedDates.has(dateKey);
                    const isLocked = isPast || hasShift;

                    return (
                      <TableCell
                        key={dateIndex}
                        sx={{
                          minWidth: 120,
                          bgcolor: hasShift
                            ? 'primary.50'
                            : hasError
                            ? 'error.lighter'
                            : isOff
                            ? 'grey.300'
                            : hasLocalData || availability
                            ? 'success.lighter'
                            : 'grey.50',
                          p: 2,
                          border: hasShift ? 2 : hasError ? 2 : hasCellChanges ? 2 : 0,
                          borderColor: hasShift
                            ? 'primary.main'
                            : hasError
                            ? 'error.main'
                            : hasCellChanges
                            ? 'warning.main'
                            : 'transparent',
                          opacity: isPast ? 0.5 : 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            alignItems: 'center',
                          }}
                        >
                          {/* Shift indicator (locked) */}
                          {hasShift && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <Lock sx={{ fontSize: 16, color: 'primary.main' }} />
                              <Typography variant="caption" color="primary.main" fontWeight={600}>
                                Shift Scheduled
                              </Typography>
                            </Box>
                          )}

                          {/* Hours Input or Simple Toggle */}
                          {allowSetHours ? (
                            <TextField
                              id={`hours-input-${dateIndex}`}
                              type="number"
                              value={hoursValue}
                              onChange={(e) => handleHoursChange(date, e.target.value)}
                              onFocus={(e) => e.target.select()}
                              onKeyDown={(e) => handleKeyDown(e, dateIndex)}
                              disabled={isLocked}
                              size="small"
                              placeholder="Hours"
                              error={hasError}
                              helperText={hoursError}
                              inputProps={{
                                min: 0,
                                max: 24,
                                step: 0.5,
                                style: { textAlign: 'center' },
                                tabIndex: isLocked ? -1 : 0,
                              }}
                              sx={{
                                width: '100%',
                                '& .MuiInputBase-root': {
                                  fontSize: '1rem',
                                },
                              }}
                            />
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                              <Switch
                                checked={isDateAvailable(date)}
                                onChange={(e) => handleAvailabilityToggle(date, e.target.checked)}
                                disabled={isLocked}
                                color="success"
                              />
                              <Typography
                                variant="caption"
                                color={isDateAvailable(date) ? 'success.main' : 'text.secondary'}
                                fontWeight={isDateAvailable(date) ? 600 : 400}
                              >
                                {isDateAvailable(date) ? 'Available' : 'Not Available'}
                              </Typography>
                            </Box>
                          )}

                          {/* OFF/Holiday indicator (only in hours mode) */}
                          {allowSetHours && isOff && !hasShift && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontStyle: 'italic' }}
                              >
                                OFF/Holiday
                              </Typography>
                              {!isLocked && hasCellChanges && (
                                <Tooltip title="Revert this day">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRevertCell(date)}
                                    color="warning"
                                    sx={{ p: 0.25 }}
                                  >
                                    <Undo fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          )}

                          {/* Preferences Info & Button */}
                          {!isOff && !hasShift && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.5,
                                width: '100%',
                              }}
                            >
                              {allowSetTimeRestrictions && restrictionLabel && (
                                <Chip
                                  label={restrictionLabel}
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              )}
                              {!isLocked && (
                                <>
                                  {allowSetTimeRestrictions && (
                                    <Tooltip title="Set time restrictions">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleOpenPreferences(date)}
                                        sx={{ p: 0.25 }}
                                      >
                                        <Settings fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {hasCellChanges && (
                                    <Tooltip title="Revert this day">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleRevertCell(date)}
                                        color="warning"
                                        sx={{ p: 0.25 }}
                                      >
                                        <Undo fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </>
                              )}
                            </Box>
                          )}

                          {/* Past date indicator */}
                          {isPast && !hasShift && (
                            <Typography variant="caption" color="text.disabled">
                              Past
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Preferences Dialog */}
      <Dialog
        open={preferencesOpen}
        onClose={() => setPreferencesOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: { xs: 0, sm: 3 }, m: { xs: 0, sm: 2 } },
        }}
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pb: 1, pr: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Schedule color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
            <Typography variant="h5" fontWeight={600} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              Time Restrictions
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            Set your availability for{' '}
            <strong>
              {selectedDate?.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'short',
                day: 'numeric',
              })}
            </strong>
          </Typography>
          <IconButton
            onClick={() => setPreferencesOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2, px: { xs: 2, sm: 3 } }}>
          {/* Quick Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, mb: 2.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mr: 0.5, display: { xs: 'none', sm: 'block' } }}>
              QUICK ACTIONS:
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={() =>
                setPreferencesForm({
                  ...preferencesForm,
                  timeRestrictions: { morning: true, afternoon: true, evening: true, night: true },
                })
              }
              startIcon={<CheckCircle fontSize="small" />}
              sx={{ minWidth: 'auto', fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
            >
              Select All
            </Button>
            <Divider orientation="vertical" flexItem />
            <Button
              size="small"
              variant="text"
              onClick={() =>
                setPreferencesForm({
                  ...preferencesForm,
                  timeRestrictions: { morning: false, afternoon: false, evening: false, night: false },
                })
              }
              startIcon={<RadioButtonUnchecked fontSize="small" />}
              sx={{ minWidth: 'auto', fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
            >
              Clear All
            </Button>
          </Box>

          {/* Time Periods */}
          <Stack spacing={{ xs: 1, sm: 1.5 }}>
            {/* Morning */}
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: 2,
                transition: 'all 0.2s',
                bgcolor: preferencesForm.timeRestrictions.morning ? 'background.paper' : 'grey.50',
                borderLeft: preferencesForm.timeRestrictions.morning ? 4 : 0,
                borderLeftColor: '#4C7EDB',
                '&:hover': { boxShadow: 1 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
                <Box
                  sx={{
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 },
                    borderRadius: 2,
                    bgcolor: preferencesForm.timeRestrictions.morning ? 'rgba(76, 126, 219, 0.1)' : 'grey.200',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <LightMode sx={{ color: preferencesForm.timeRestrictions.morning ? '#4C7EDB' : 'text.disabled', fontSize: { xs: 20, sm: 24 } }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ color: preferencesForm.timeRestrictions.morning ? '#4C7EDB' : 'text.secondary', fontSize: { xs: '0.9rem', sm: '1rem' } }}
                  >
                    Morning
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    6:00 AM - 12:00 PM
                  </Typography>
                </Box>
                <Switch
                  checked={preferencesForm.timeRestrictions.morning}
                  onChange={(e) =>
                    setPreferencesForm({
                      ...preferencesForm,
                      timeRestrictions: { ...preferencesForm.timeRestrictions, morning: e.target.checked },
                    })
                  }
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#4C7EDB' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4C7EDB' },
                  }}
                />
              </Box>
            </Paper>

            {/* Afternoon */}
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: 2,
                transition: 'all 0.2s',
                bgcolor: preferencesForm.timeRestrictions.afternoon ? 'background.paper' : 'grey.50',
                borderLeft: preferencesForm.timeRestrictions.afternoon ? 4 : 0,
                borderLeftColor: '#D9A441',
                '&:hover': { boxShadow: 1 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
                <Box
                  sx={{
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 },
                    borderRadius: 2,
                    bgcolor: preferencesForm.timeRestrictions.afternoon ? 'rgba(217, 164, 65, 0.1)' : 'grey.200',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <WbTwilight sx={{ color: preferencesForm.timeRestrictions.afternoon ? '#D9A441' : 'text.disabled', fontSize: { xs: 20, sm: 24 } }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ color: preferencesForm.timeRestrictions.afternoon ? '#D9A441' : 'text.secondary', fontSize: { xs: '0.9rem', sm: '1rem' } }}
                  >
                    Afternoon
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    12:00 PM - 6:00 PM
                  </Typography>
                </Box>
                <Switch
                  checked={preferencesForm.timeRestrictions.afternoon}
                  onChange={(e) =>
                    setPreferencesForm({
                      ...preferencesForm,
                      timeRestrictions: { ...preferencesForm.timeRestrictions, afternoon: e.target.checked },
                    })
                  }
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#D9A441' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#D9A441' },
                  }}
                />
              </Box>
            </Paper>

            {/* Evening */}
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: 2,
                transition: 'all 0.2s',
                bgcolor: preferencesForm.timeRestrictions.evening ? 'background.paper' : 'grey.50',
                borderLeft: preferencesForm.timeRestrictions.evening ? 4 : 0,
                borderLeftColor: '#7B61B3',
                '&:hover': { boxShadow: 1 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
                <Box
                  sx={{
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 },
                    borderRadius: 2,
                    bgcolor: preferencesForm.timeRestrictions.evening ? 'rgba(123, 97, 179, 0.1)' : 'grey.200',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Nightlight sx={{ color: preferencesForm.timeRestrictions.evening ? '#7B61B3' : 'text.disabled', fontSize: { xs: 20, sm: 24 } }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ color: preferencesForm.timeRestrictions.evening ? '#7B61B3' : 'text.secondary', fontSize: { xs: '0.9rem', sm: '1rem' } }}
                  >
                    Evening
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    6:00 PM - 12:00 AM
                  </Typography>
                </Box>
                <Switch
                  checked={preferencesForm.timeRestrictions.evening}
                  onChange={(e) =>
                    setPreferencesForm({
                      ...preferencesForm,
                      timeRestrictions: { ...preferencesForm.timeRestrictions, evening: e.target.checked },
                    })
                  }
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#7B61B3' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#7B61B3' },
                  }}
                />
              </Box>
            </Paper>

            {/* Night */}
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: 2,
                transition: 'all 0.2s',
                bgcolor: preferencesForm.timeRestrictions.night ? 'background.paper' : 'grey.50',
                borderLeft: preferencesForm.timeRestrictions.night ? 4 : 0,
                borderLeftColor: '#2F3A56',
                '&:hover': { boxShadow: 1 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
                <Box
                  sx={{
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 },
                    borderRadius: 2,
                    bgcolor: preferencesForm.timeRestrictions.night ? 'rgba(47, 58, 86, 0.1)' : 'grey.200',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <DarkMode sx={{ color: preferencesForm.timeRestrictions.night ? '#2F3A56' : 'text.disabled', fontSize: { xs: 20, sm: 24 } }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ color: preferencesForm.timeRestrictions.night ? '#2F3A56' : 'text.secondary', fontSize: { xs: '0.9rem', sm: '1rem' } }}
                  >
                    Night
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    12:00 AM - 6:00 AM
                  </Typography>
                </Box>
                <Switch
                  checked={preferencesForm.timeRestrictions.night}
                  onChange={(e) =>
                    setPreferencesForm({
                      ...preferencesForm,
                      timeRestrictions: { ...preferencesForm.timeRestrictions, night: e.target.checked },
                    })
                  }
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#2F3A56' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#2F3A56' },
                  }}
                />
              </Box>
            </Paper>
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, mb: 2.5 }}>
            Leave all unchecked if available any time. Select multiple for custom combinations.
          </Typography>

          {/* Notes */}
          <TextField
            label="Notes (Optional)"
            multiline
            rows={3}
            value={preferencesForm.notes}
            onChange={(e) => setPreferencesForm({ ...preferencesForm, notes: e.target.value })}
            fullWidth
            placeholder="Any additional notes for this day..."
          />
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button
            onClick={() => setPreferencesOpen(false)}
            size="large"
            fullWidth={isMobile}
            sx={{ order: { xs: 2, sm: 1 } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSavePreferences}
            variant="contained"
            size="large"
            fullWidth={isMobile}
            sx={{ minWidth: { xs: 'auto', sm: 120 }, order: { xs: 1, sm: 2 } }}
          >
            Set
          </Button>
        </DialogActions>
      </Dialog>
    </EmployeeLayout>
  );
}
